// =============================================================================
// VandieZ shipper-web -- Trip detail / tracking (/trips/:tripId)
// =============================================================================
// Shows live trip state for an accepted load:
//   * Status timeline (booked → en route to pickup → picked up → in transit
//     → delivered → completed).
//   * Driver + vehicle metadata + revealed contact PII.
//   * Latest GPS ping with timestamp + lat/lng + speed.
//
// GPS uses polling (15s) rather than the WS subscriber the mobile app uses
// because the WS surface isn't open through the web origin yet. Polling is
// good enough for an MVP — under 5 KB/min per session.
//
// Map: deferred. Showing a map needs a tile provider key (mapbox/maptiler) +
// ~150 KB of JS. For the MVP we show the raw coordinates + a clickable
// "Open in Google Maps" link.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  MapPin,
  Navigation,
  Phone,
  Truck,
} from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { tripsApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { GpsPing, Trip, TripStatus } from "@/types/trip";

const STATUS_FLOW: TripStatus[] = [
  "booked",
  "en_route_to_pickup",
  "arrived_at_pickup",
  "loading",
  "in_transit",
  "arrived_at_drop",
  "unloading",
  "delivered",
  "completed",
];

const STATUS_LABEL: Record<TripStatus, string> = {
  booked: "Booked",
  en_route_to_pickup: "En route to pickup",
  arrived_at_pickup: "At pickup",
  loading: "Loading",
  in_transit: "In transit",
  arrived_at_drop: "At drop",
  unloading: "Unloading",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
};

function formatDate(iso: string | null): string {
  if (iso === null) {return "—";}
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatINR(amount: string): string {
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) {return amount;}
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function statusIndex(s: TripStatus): number {
  const i = STATUS_FLOW.indexOf(s);
  return i === -1 ? -1 : i;
}

export function TripDetailPage(): React.JSX.Element {
  const { tripId } = useParams<{ tripId: string }>();
  const { accessToken } = useAuth();

  const [trip, setTrip] = useState<Trip | null>(null);
  const [lastPing, setLastPing] = useState<GpsPing | null>(null);
  const [pingsTotal, setPingsTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Use a ref for the polling timer so the cleanup function in useEffect
  // can clear it even if the latest closure changes.
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (accessToken === null || tripId === undefined) {return;}
    const controller = new AbortController();
    let cancelled = false;
    // Capture as locals so the inner closure has narrowed types and
    // doesn't need non-null assertions on every call.
    const token = accessToken;
    const id = tripId;

    // Tracks the most recent fetched trip across iterations of the loop so
    // the next-poll decision uses fresh state, not the captured initial
    // closure (which is always null on first call).
    let latest: Trip | null = null;

    async function pollOnce(): Promise<void> {
      if (cancelled) {return;}
      try {
        const t = await tripsApi.detail(token, id, controller.signal);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated by cleanup
      if (cancelled) {return;}
        latest = t;
        setTrip(t);
        setError(null);
        if (
          t.status !== "booked"
          && t.status !== "cancelled"
          && t.status !== "completed"
        ) {
          try {
            const gps = await tripsApi.gpsHistory(
              token,
              id,
              controller.signal,
            );
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated by cleanup
      if (cancelled) {return;}
            setPingsTotal(gps.total);
            setLastPing(gps.items[0] ?? null);
          } catch {
            // GPS being absent is non-fatal; the trip view stays up.
          }
        }
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated by cleanup
        if (cancelled || controller.signal.aborted) {return;}
        setError(
          err instanceof ApiError ? err.problem.detail : "Couldn't load trip.",
        );
      } finally {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated by cleanup
        if (!cancelled) {
          setIsLoading(false);
          const s = latest?.status;
          if (s !== "completed" && s !== "cancelled" && s !== "delivered") {
            pollTimerRef.current = setTimeout(() => { void pollOnce(); }, 15_000);
          }
        }
      }
    }

    void pollOnce();
    return () => {
      cancelled = true;
      controller.abort();
      if (pollTimerRef.current !== null) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [accessToken, tripId]);

  if (isLoading) {
    return (
      <div
        role="status"
        className="flex min-h-screen items-center justify-center"
      >
        <Loader2
          aria-hidden="true"
          className="h-6 w-6 animate-spin text-muted-foreground"
        />
        <span className="sr-only">Loading trip…</span>
      </div>
    );
  }

  if (trip === null) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-10">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error ?? "Trip not found."}</AlertDescription>
        </Alert>
        <Link to="/" className="mt-4 inline-block">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4" />
            Back to my loads
          </Button>
        </Link>
      </div>
    );
  }

  const currentIdx = statusIndex(trip.status);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Trip tracking"
        actions={
          <Link to={`/loads/${trip.load_id}`}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
              Back to load
            </Button>
          </Link>
        }
      />

      <div className="space-y-6">
        {trip.status === "cancelled" ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This trip was cancelled at {formatDate(trip.cancelled_at)}.
            </AlertDescription>
          </Alert>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Status</CardTitle>
            <CardDescription>
              {trip.status === "completed" || trip.status === "cancelled"
                ? "This trip is no longer active."
                : "Auto-refreshes every 15s."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3">
              {STATUS_FLOW.map((s, i) => {
                const done = currentIdx >= i;
                const active = s === trip.status;
                return (
                  <li key={s} className="flex items-center gap-3">
                    {done ? (
                      <CheckCircle2 className="h-5 w-5 text-success" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground" />
                    )}
                    <span
                      className={`text-sm ${active ? "font-semibold" : done ? "text-foreground" : "text-muted-foreground"}`}
                    >
                      {STATUS_LABEL[s]}
                    </span>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4" />
              Live location
            </CardTitle>
            <CardDescription>
              {lastPing === null
                ? "Driver hasn't pinged yet."
                : `Last update ${formatDate(lastPing.recorded_at)} · ${pingsTotal} pings total`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {lastPing === null ? (
              <p className="text-sm text-muted-foreground">
                GPS pings start once the driver is en route to pickup.
              </p>
            ) : (
              <div className="space-y-2 text-sm">
                <p className="font-mono">
                  {lastPing.lat}, {lastPing.lng}
                </p>
                {lastPing.speed_kmh !== null ? (
                  <p className="text-xs text-muted-foreground">
                    Moving at {Math.round(lastPing.speed_kmh)} km/h
                  </p>
                ) : null}
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${lastPing.lat},${lastPing.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Navigation className="h-3 w-3" />
                  Open in Google Maps
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Truck className="h-4 w-4" />
              Trip contacts
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Pickup
              </p>
              <p className="text-sm font-medium">
                {trip.pickup_contact_name ?? "—"}
              </p>
              {trip.pickup_contact_phone !== null ? (
                <a
                  href={`tel:${trip.pickup_contact_phone}`}
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Phone className="h-3 w-3" />
                  {trip.pickup_contact_phone}
                </a>
              ) : null}
              <p className="font-mono text-xs text-muted-foreground">
                {trip.pickup_lat}, {trip.pickup_lng}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Drop
              </p>
              <p className="text-sm font-medium">
                {trip.drop_contact_name ?? "—"}
              </p>
              {trip.drop_contact_phone !== null ? (
                <a
                  href={`tel:${trip.drop_contact_phone}`}
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Phone className="h-3 w-3" />
                  {trip.drop_contact_phone}
                </a>
              ) : null}
              <p className="font-mono text-xs text-muted-foreground">
                {trip.drop_lat}, {trip.drop_lng}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-4 w-4" />
              Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Booked</dt>
                <dd>{formatDate(trip.booked_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Picked up</dt>
                <dd>{formatDate(trip.picked_up_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Delivered</dt>
                <dd>{formatDate(trip.delivered_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Completed</dt>
                <dd>{formatDate(trip.completed_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Driver</dt>
                <dd>{trip.driver_name ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Driver phone</dt>
                <dd>
                  {trip.driver_phone === null ? (
                    "—"
                  ) : (
                    // A tel: link rather than plain text. On a phone this
                    // dials; on a desktop it is still selectable. Matching
                    // how mobile-shipper already reaches a driver during
                    // bid review, so this is not a new disclosure -- it is
                    // the same one at the point it is most needed.
                    <a
                      className="text-primary underline"
                      href={`tel:${trip.driver_phone}`}
                    >
                      {trip.driver_phone}
                    </a>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Vehicle</dt>
                <dd>
                  {trip.vehicle_registration_number ?? "—"}
                  {trip.vehicle_display_name !== null
                    ? ` · ${trip.vehicle_display_name}`
                    : ""}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Trip value</dt>
                <dd>{formatINR(trip.trip_value)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Platform commission</dt>
                <dd>{formatINR(trip.platform_commission_inr)}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
